import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../popups.css';

/** Same breakpoint the layout uses to switch the sidebar into the mobile rail/drawer. */
export const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

export function isMobileViewport() {
  return (
    typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches
  );
}

/** Notices shown instead of the feature: Family Tree only on mobile, Reports/Forms/Family News/Family History/FUN FACTS everywhere. */
export const FAMILY_TREE_MOBILE_NOTICE = {
  title: 'Family Tree',
  message:
    'The family tree is currently being revised to work with mobile device platforms and won’t be available for a while. If you wish to see the full scale version of the family tree, you may view on a tablet, desktop computer or laptop.',
};
export const REPORTS_NOTICE = {
  title: 'Reports',
  message:
    'This section is currently being developed and will be available in the next upgrade set for late December 2026 or early 2027.',
};
export const FORMS_NOTICE = {
  title: 'Forms',
  message:
    'This section is currently being developed and will be available very soon.',
};
export const FAMILY_NEWS_NOTICE = {
  title: 'Family News',
  message:
    'This section is currently being developed and will be available very soon.',
};
export const FAMILY_HISTORY_NOTICE = {
  title: 'Family History',
  message:
    'This section is currently being developed and will be available very soon.',
};
export const FUN_FACTS_NOTICE = {
  title: 'FUN FACTS',
  message:
    'This section is currently being developed and will be available very soon.',
};

/**
 * Raised by anything that would have navigated to the family tree on a phone.
 * A single host in the layout answers it, so every entry point shows one modal
 * without each call site owning one.
 */
export const OPEN_FAMILY_TREE_MOBILE_NOTICE_EVENT = 'openFamilyTreeMobileNotice';

/**
 * Call before navigating to /family-tree. On a phone it raises the notice and
 * returns true, meaning the caller must stay put; on desktop it returns false
 * and the caller navigates as usual.
 */
export function blockFamilyTreeNavigationOnMobile() {
  if (!isMobileViewport()) return false;
  window.dispatchEvent(new Event(OPEN_FAMILY_TREE_MOBILE_NOTICE_EVENT));
  return true;
}

/**
 * Explains why a feature is unavailable. Portalled to the body: both the mobile
 * sidebar and the family tree canvas are their own stacking contexts, which
 * would trap a fixed overlay behind the page.
 */
function FeatureNoticeModal({ title, message, onClose }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="sidebar-notice-overlay" role="presentation" onClick={onClose}>
      <div
        className="sidebar-notice-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sidebar-notice-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="sidebar-notice-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h3 className="sidebar-notice-title" id="sidebar-notice-title">
          {title}
        </h3>
        <p className="sidebar-notice-text">{message}</p>
        <button type="button" className="sidebar-notice-btn" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>,
    document.body
  );
}

/** Mounted once in the layout; shows the mobile family tree notice on request. */
export function FamilyTreeMobileNoticeHost() {
  const [isOpen, setIsOpen] = useState(false);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener(OPEN_FAMILY_TREE_MOBILE_NOTICE_EVENT, open);
    return () =>
      window.removeEventListener(OPEN_FAMILY_TREE_MOBILE_NOTICE_EVENT, open);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
    // The tree opens fine once the window is desktop-sized, so drop the notice there
    const apply = () => {
      if (!mq.matches) setIsOpen(false);
    };
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  if (!isOpen) return null;

  return (
    <FeatureNoticeModal
      title={FAMILY_TREE_MOBILE_NOTICE.title}
      message={FAMILY_TREE_MOBILE_NOTICE.message}
      onClose={close}
    />
  );
}

export default FeatureNoticeModal;
